import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Music, Mic, Layers, Upload, Loader2, Download, Trash2, Sparkles, FileAudio,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import {
  startAudioJob, getJobStatus, listMyJobs, deleteJob,
} from "@/lib/ai-studio.functions";

export const Route = createFileRoute("/_authenticated/ai-studio")({
  head: () => ({
    meta: [
      { title: "AI Studio · Surer Adda" },
      { name: "description", content: "AI vocal remover, stem splitter and audio transcriber for Surer Adda members." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AiStudio,
});

type Tool = "vocal_remove" | "stems" | "transcribe";

const TOOLS: { id: Tool; title: string; desc: string; icon: typeof Music; color: string }[] = [
  {
    id: "vocal_remove",
    title: "Vocal Remover",
    desc: "Upload a song → get clean vocals and a karaoke-style backing track.",
    icon: Mic,
    color: "from-rose-500/20 to-rose-500/5",
  },
  {
    id: "stems",
    title: "Stem Splitter",
    desc: "Split a song into 4 tracks: vocals, drums, bass and other.",
    icon: Layers,
    color: "from-amber-500/20 to-amber-500/5",
  },
  {
    id: "transcribe",
    title: "Audio Transcriber",
    desc: "Transcribe voice-overs, interviews and lyrics with timestamps.",
    icon: FileAudio,
    color: "from-emerald-500/20 to-emerald-500/5",
  },
];

const MAX_BYTES = 50 * 1024 * 1024; // 50 MB

function AiStudio() {
  const qc = useQueryClient();
  const startFn = useServerFn(startAudioJob);
  const pollFn = useServerFn(getJobStatus);
  const listFn = useServerFn(listMyJobs);
  const deleteFn = useServerFn(deleteJob);

  const [tool, setTool] = useState<Tool>("vocal_remove");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: jobs } = useQuery({
    queryKey: ["ai-jobs"],
    queryFn: () => listFn(),
    refetchInterval: 5000,
  });

  // Poll active job
  useEffect(() => {
    if (!activeJobId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const j = await pollFn({ data: { jobId: activeJobId } });
        if (cancelled) return;
        if (j.status === "succeeded" || j.status === "failed") {
          setActiveJobId(null);
          qc.invalidateQueries({ queryKey: ["ai-jobs"] });
          if (j.status === "succeeded") toast.success("Done! Your files are ready.");
          else toast.error(j.error || "Job failed");
          return;
        }
      } catch (e) {
        // keep polling on transient errors
        console.warn(e);
      }
      if (!cancelled) setTimeout(tick, 3000);
    };
    const t = setTimeout(tick, 1500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [activeJobId, pollFn, qc]);

  const runMutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("Please choose an audio file first.");
      if (file.size > MAX_BYTES) throw new Error("File is too large (max 50 MB).");
      setUploading(true);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("Not signed in");
        const safeName = file.name.replace(/[^\w.\-]+/g, "_");
        const path = `${user.id}/${crypto.randomUUID()}-${safeName}`;
        const { error: upErr } = await supabase.storage
          .from("ai-studio-uploads")
          .upload(path, file, { contentType: file.type || "audio/mpeg", upsert: false });
        if (upErr) throw new Error(upErr.message);
        const res = await startFn({ data: { tool, input_path: path, input_filename: file.name } });
        return res.jobId;
      } finally {
        setUploading(false);
      }
    },
    onSuccess: (jobId) => {
      setActiveJobId(jobId);
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      qc.invalidateQueries({ queryKey: ["ai-jobs"] });
      toast.success("Uploaded — processing started. This can take a few minutes.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const onDelete = async (id: string) => {
    await deleteFn({ data: { jobId: id } });
    qc.invalidateQueries({ queryKey: ["ai-jobs"] });
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SiteHeader />
      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-8 sm:px-6">
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Sparkles className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold sm:text-3xl">AI Studio</h1>
            <p className="text-sm text-muted-foreground">
              Vocal remover, stem splitter, and audio transcriber — for members.
            </p>
          </div>
        </div>

        {/* Tool picker */}
        <div className="grid gap-3 sm:grid-cols-3">
          {TOOLS.map((t) => {
            const Icon = t.icon;
            const active = tool === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTool(t.id)}
                className={cn(
                  "rounded-xl border p-4 text-left transition",
                  "bg-gradient-to-br",
                  t.color,
                  active ? "border-primary ring-2 ring-primary/40" : "border-border hover:border-primary/40",
                )}
              >
                <Icon className="mb-2 h-5 w-5 text-primary" />
                <p className="font-semibold">{t.title}</p>
                <p className="mt-1 text-xs text-muted-foreground">{t.desc}</p>
              </button>
            );
          })}
        </div>

        {/* Upload card */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-base">Upload audio</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <label
              className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border p-8 text-center hover:border-primary/50"
            >
              <input
                ref={inputRef}
                type="file"
                accept="audio/*"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
              <Upload className="h-6 w-6 text-muted-foreground" />
              <p className="text-sm font-medium">
                {file ? file.name : "Click to choose an MP3, WAV or M4A file"}
              </p>
              <p className="text-xs text-muted-foreground">Max 50 MB · songs up to ~15 minutes work best</p>
            </label>

            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-muted-foreground">
                Processing usually takes 1–5 minutes. You can leave the page — jobs continue in the background.
              </p>
              <Button
                onClick={() => runMutation.mutate()}
                disabled={!file || uploading || !!activeJobId || runMutation.isPending}
              >
                {uploading || runMutation.isPending ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading…</>
                ) : activeJobId ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing…</>
                ) : (
                  <><Sparkles className="mr-2 h-4 w-4" /> Run {TOOLS.find((t) => t.id === tool)?.title}</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Jobs */}
        <div className="mt-10">
          <h2 className="mb-3 text-lg font-semibold">Your jobs</h2>
          {!jobs?.length ? (
            <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              No jobs yet. Upload an audio file above to get started.
            </p>
          ) : (
            <div className="space-y-3">
              {jobs.map((j) => <JobRow key={j.id} job={j} onDelete={() => onDelete(j.id)} />)}
            </div>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

type Job = Awaited<ReturnType<typeof listMyJobs>>[number];

function JobRow({ job, onDelete }: { job: Job; onDelete: () => void }) {
  const toolInfo = TOOLS.find((t) => t.id === job.tool);
  const urls = (job.output_urls ?? {}) as Record<string, string>;
  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <p className="font-medium">{toolInfo?.title ?? job.tool}</p>
            <p className="text-xs text-muted-foreground">{job.input_filename}</p>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={job.status} />
            <Button variant="ghost" size="icon" onClick={onDelete} aria-label="Delete job">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {job.status === "processing" || job.status === "queued" ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Working on it…
          </div>
        ) : null}

        {job.status === "failed" ? (
          <p className="text-sm text-destructive">{job.error || "Job failed"}</p>
        ) : null}

        {job.status === "succeeded" && job.tool === "transcribe" ? (
          <div className="space-y-2">
            <pre className="max-h-60 overflow-auto rounded-md bg-muted p-3 text-sm whitespace-pre-wrap">
              {job.transcript || "(empty transcript)"}
            </pre>
            <div className="flex flex-wrap gap-2">
              {urls.txt && <DownloadLink href={urls.txt} label="Transcript .txt" />}
              {urls.srt && <DownloadLink href={urls.srt} label="Subtitles .srt" />}
            </div>
          </div>
        ) : null}

        {job.status === "succeeded" && job.tool !== "transcribe" ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {Object.entries(urls).map(([stem, url]) => (
              <div key={stem} className="rounded-md border border-border p-3">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {stem.replace(/_/g, " ")}
                </p>
                <audio controls src={url} className="w-full" />
                <a
                  href={url}
                  download
                  className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                >
                  <Download className="h-3 w-3" /> Download
                </a>
              </div>
            ))}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    queued: "bg-muted text-foreground",
    processing: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
    succeeded: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
    failed: "bg-destructive/15 text-destructive",
  };
  return <Badge className={cn("capitalize", map[status] ?? "")}>{status}</Badge>;
}

function DownloadLink({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      download
      className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-accent"
    >
      <Download className="h-3 w-3" /> {label}
    </a>
  );
}
